import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  Cpu,
  Globe,
  Lightbulb,
  Network,
  Target,
  Sparkles,
  MapPin,
  ArrowUpRight,
  Activity,
} from "lucide-react";

const whatWeDoItems = [
  {
    icon: Brain,
    title: "AI-Powered Health Solutions",
    description:
      "Standard and agentic AI revolutionizing diagnostics, clinical decision support, and personalized patient care pathways.",
    img: "/feature_ai_healthcare.png",
    accent: "text-primary",
    bg: "bg-primary/10",
  },
  {
    icon: Cpu,
    title: "Clinical SaaS Platforms",
    description:
      "Purpose-built software for doctors, clinics, hospitals, and diagnostic centers — from prescription to billing, all in one place.",
    img: "/feature_clinical_saas.png",
    accent: "text-secondary",
    bg: "bg-secondary/10",
  },
  {
    icon: Activity,
    title: "Connected Devices & IoT",
    description:
      "IoT-enabled medical devices for continuous, real-time health monitoring and seamless data collection at the point of care.",
    img: "/feature_connected_devices.png",
    accent: "text-accent",
    bg: "bg-accent/10",
  },
  {
    icon: Globe,
    title: "Healthcare APIs & Integrations",
    description:
      "Robust, standards-compliant APIs enabling seamless integration and clinical data exchange across medical ecosystems.",
    img: "/feature_healthcare_apis.png",
    accent: "text-primary",
    bg: "bg-primary/10",
  },
];

const principles = [
  {
    icon: Lightbulb,
    title: "Innovation",
    description:
      "We explore AI and automation to solve deeply rooted healthcare challenges, pushing boundaries to bring novel solutions to clinicians and patients.",
    accent: "text-primary",
    bg: "bg-primary/10",
    bar: "from-primary to-primary/40",
  },
  {
    icon: Network,
    title: "Integration",
    description:
      "Our platforms work seamlessly across diverse medical systems and data sources — connecting doctors, patients, devices, and data into one coherent ecosystem.",
    accent: "text-secondary",
    bg: "bg-secondary/10",
    bar: "from-secondary to-secondary/40",
  },
  {
    icon: Target,
    title: "Impact",
    description:
      "Technology is meaningful only when it improves lives — one patient, one doctor, one community at a time. Every feature we ship is measured by its real-world impact.",
    accent: "text-accent",
    bg: "bg-accent/10",
    bar: "from-accent to-accent/40",
  },
];

const About = () => {
  return (
    <Layout>
      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/bg_main.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/75 to-secondary/80" />
        </div>

        <div className="relative container mx-auto px-4 text-center text-white">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/30 text-white/90 text-sm font-semibold mb-8">
            <Sparkles className="h-3.5 w-3.5" />
            About Wellnext Technologies
          </div>

          <div className="flex justify-center mb-8">
            <img
              src="/WNTPL_Full_Logo.svg"
              alt="Wellnext Technologies"
              className="h-14 brightness-0 invert"
            />
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1]">
            Transforming healthcare
            <br />
            through <span className="text-white/80 italic">technology</span>
          </h1>

          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-12 leading-relaxed">
            We stand at the intersection of healthcare, data science, and artificial intelligence
            — building tools that empower clinicians, connect patients, and improve outcomes for
            all.
          </p>

          <div className="flex flex-wrap justify-center gap-5">
            {[
              { value: "30+", label: "Years of engineering experience" },
              { value: "2", label: "Purpose-built SaaS platforms" },
              { value: "AI‑first", label: "Product philosophy" },
            ].map(({ value, label }) => (
              <div
                key={value}
                className="text-center px-6 py-4 rounded-2xl bg-white/10 backdrop-blur border border-white/20"
              >
                <p className="text-3xl font-bold text-white">{value}</p>
                <p className="text-sm text-white/70 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Mission & Vision ─────────────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <div>
              <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
                Our Mission
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                Empowering every stakeholder in healthcare
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Wellnext Technologies is a forward-thinking health-tech company committed to
                transforming the way healthcare is delivered, experienced, and managed. Our mission
                is to empower healthcare professionals, organizations, and individuals through
                intelligent, data-driven technologies that promote better health outcomes and
                community well-being.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                We design and implement integrated health solutions that bring doctors, diagnostic
                centers, and patients onto a unified digital platform — making healthcare smarter,
                faster, and more connected.
              </p>
              <blockquote className="border-l-4 border-primary pl-5 py-1">
                <p className="text-xl font-semibold text-foreground italic leading-relaxed">
                  "Together, we are building the next generation of healthcare — intelligent,
                  inclusive, and truly well."
                </p>
              </blockquote>
            </div>

            {/* Image */}
            <div className="relative pb-6">
              <div className="rounded-3xl overflow-hidden shadow-[0_20px_60px_-12px_rgba(0,0,0,0.18)]">
                <img
                  src="/wellnext-about-multi-connectivity.png"
                  alt="AI and healthcare interaction"
                  className="w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-2 -left-2 flex items-center gap-2.5 bg-white rounded-2xl shadow-xl px-5 py-3 border border-border/40">
                <Brain className="h-5 w-5 text-primary shrink-0" />
                <div>
                  <p className="text-xs font-bold text-foreground">AI at the core</p>
                  <p className="text-xs text-muted-foreground">Every product, every layer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── What We Build ────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-b from-muted/40 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              What We Build
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient-primary">Four technology pillars</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              From AI diagnostics to connected devices — our innovations span the full healthcare
              delivery chain.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whatWeDoItems.map(({ icon: Icon, title, description, img, accent, bg }) => (
              <div
                key={title}
                className="group overflow-hidden rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] transition-all duration-500 flex flex-col"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={img}
                    alt={title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/20 to-transparent" />
                  <div
                    className={`absolute top-4 left-4 h-10 w-10 rounded-2xl ${bg} flex items-center justify-center shadow-sm`}
                  >
                    <Icon className={`h-5 w-5 ${accent}`} />
                  </div>
                </div>
                <div className="p-6 flex-1">
                  <h3 className="text-lg font-bold text-foreground mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Core Principles ──────────────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              Our Principles
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient-primary">Innovation.</span>{" "}
              <span className="text-gradient-primary">Integration.</span>{" "}
              <span className="text-gradient-primary">Impact.</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Three principles that define how we approach every product, every partnership, and
              every decision.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {principles.map(({ icon: Icon, title, description, accent, bg, bar }) => (
              <div
                key={title}
                className="group rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                <div className={`h-[3px] w-full bg-gradient-to-r ${bar}`} />
                <div className="p-8">
                  <div
                    className={`h-12 w-12 rounded-2xl ${bg} flex items-center justify-center mb-5`}
                  >
                    <Icon className={`h-6 w-6 ${accent}`} />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-3">{title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Made in India ────────────────────────────────────────────────── */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/feature_made_in_india.png"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/92 via-primary/78 to-secondary/75" />
        </div>
        <div className="relative container mx-auto px-4 text-center text-white">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/30 text-white/90 text-sm font-semibold mb-6">
            <MapPin className="h-3.5 w-3.5" />
            Built in India, for the world
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 max-w-3xl mx-auto leading-tight">
            Solving India's healthcare challenges with world-class technology
          </h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Our product development philosophy centers on making advanced healthcare technology
            affordable and accessible — built with pride in India, designed to serve clinicians
            and patients everywhere.
          </p>
        </div>
      </section>

      {/* ─── Leadership ───────────────────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              Leadership
            </span>
            <h2 className="text-4xl font-bold">The person behind the vision</h2>
          </div>

          <div className="max-w-4xl mx-auto rounded-3xl overflow-hidden border border-border/60 shadow-[0_8px_40px_rgba(0,0,0,0.08)] bg-white">
            <div className="grid md:grid-cols-[280px_1fr]">
              {/* Photo */}
              <div className="relative md:min-h-[400px]">
                <img
                  src="/CEO_front_headshot.png"
                  alt="Sumantra Mukherjee – Founder & CEO"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-5 text-white">
                  <p className="text-xs uppercase tracking-widest text-white/70">Founder & CEO</p>
                  <p className="text-lg font-semibold">Sumantra Mukherjee</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <h3 className="text-2xl font-bold text-foreground mb-1">Sumantra Mukherjee</h3>
                <p className="text-primary font-semibold text-sm mb-5">
                  Founder, CEO & Chief Product Architect
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  30+ years in software engineering, spanning web and mobile development, cloud
                  architecture, large-scale databases, IoT, and embedded systems. An entrepreneur
                  for nearly 20 years, he has independently built multiple successful products —
                  owning every stage from architecture to delivery.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  In health-tech, Sumantra has worked closely with medical research teams on
                  data-intensive and analytics-driven projects, bringing a rare combination of
                  technical depth and clinical domain knowledge to Wellnext's product vision.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/founder">
                    <Button className="gap-2 rounded-xl">
                      Full Story
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <a
                    href="https://www.linkedin.com/in/smukherjee1001"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" className="gap-2 rounded-xl">
                      LinkedIn
                      <ArrowUpRight className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-secondary/5 to-background relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="absolute right-0 top-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute left-0 bottom-0 w-72 h-72 bg-secondary/8 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 relative text-center">
          <div className="max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              Let's Connect
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Partner with us to build
              <br />
              <span className="text-gradient-primary">better healthcare</span>
            </h2>
            <p className="text-muted-foreground mb-10 text-lg max-w-lg mx-auto">
              Whether you are a clinician, hospital group, investor, or technology partner — we'd
              love to hear from you.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/products">
                <Button
                  size="lg"
                  className="gap-2 rounded-xl shadow-[0_4px_14px_rgba(0,0,0,0.12)]"
                >
                  See Our Products
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/founder">
                <Button size="lg" variant="outline" className="gap-2 rounded-xl">
                  Meet the Founder
                  <ArrowUpRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
